/**
 * @file evacuationCapacityParameters.js
 * @version 1.0.0
 * @date October 10, 2026
 *
 * @description
 * Parameters of the ground evacuation capacity model (docs/documentation/SPEC_EVAC_CAPACITY.md), set from Dr. Tatar Radu MD's
 * description on 2026-10-10. CONSTANTS ONLY (APP_RULES 2.1); the calculation is in
 * calculationEngine/modules/evacuationCapacityModel.js.
 *
 * STATUS: each block says whether its values are Dr. Tatar's own figures ('PLANNER_FIGURE') or a placeholder
 * proposed by the assistant that still needs his confirmation ('PLACEHOLDER').
 */

export const CAPACITY_STATUS = Object.freeze({
  PLANNER_FIGURE: 'PLANNER_FIGURE',
  PLACEHOLDER: 'PLACEHOLDER'
});

export const GROUND_VEHICLE_CLASSES = Object.freeze({ LIGHT: 'LIGHT', HEAVY: 'HEAVY' });

/**
 * Average speed (km/h) of a loaded ambulance in the field by vehicle class and route surface.
 * Surface keys are the route surfaces of Tab 3 section B.3 (PAVED, GRAVEL, UNPAVED, TRAIL).
 * Dr. Tatar: a light ambulance does 60 km/h on paved roads and loses 20 km/h per step down; 40 km/h on unpaved
 * is the limit for crew and patient survivability. A heavy ambulance does 40 km/h paved, 30 unpaved, 20 in the field.
 * GRAVEL (between paved and unpaved) is the assistant's interpolation: PLACEHOLDER.
 */
export const GROUND_SPEED_KPH = {
  LIGHT: { PAVED: 60, GRAVEL: 50, UNPAVED: 40, TRAIL: 20 },
  HEAVY: { PAVED: 40, GRAVEL: 35, UNPAVED: 30, TRAIL: 20 },
  status: CAPACITY_STATUS.PLANNER_FIGURE,
  basis: 'Dr. Tatar 2026-10-10: light 60 km/h paved, minus 20 per step; heavy 40 / 30 / 20. GRAVEL is interpolated (placeholder).'
};

/** Time on the ground per run (minutes). The return leg is part of the 2 x distance travel time. */
export const GROUND_HANDLING_MIN = {
  load: 10,
  unload: 10,
  supplies: 10,          // loading supplies for the way back; applies to every run unless suppliesEveryRun is false
  suppliesEveryRun: true,
  status: CAPACITY_STATUS.PLANNER_FIGURE,
  basis: 'Dr. Tatar 2026-10-10: 10 min loading, 10 min unloading, about 10 min loading supplies.'
};

/**
 * Daily schedule of one vehicle: two crews on 12 h shifts (the minimum conflict shift), a crew change at each shift
 * and a refuelling stop per shift. The vehicle's working minutes are therefore 24 h minus these fixed times.
 */
export const GROUND_DAILY_SCHEDULE = {
  crewsPerVehicle: 2,
  shiftHours: 12,
  crewChangeMin: 30,             // PLACEHOLDER inside Dr. Tatar's "30 minutes to 1 hour"
  refuelMin: 15,                 // Dr. Tatar: about 15 min per unit
  minRefuelsPerVehiclePerDay: 1, // PLACEHOLDER: at least one stop a day; more when the distance driven exceeds the vehicle's range
  status: CAPACITY_STATUS.PLACEHOLDER,
  basis: 'Dr. Tatar 2026-10-10: 2 crews per vehicle per day, 12 h shifts, refuel about 15 min, crew change 30 min to 1 h. ' +
    'Crew change 30 min and the daily minimum of one refuel are the assistant\'s choices; the number of refuels beyond the ' +
    'minimum follows from the vehicle\'s range_km (ambulanceTypes.js).'
};

/**
 * Fallback loading rule, used only when a vehicle has no mixed-load figures in the constants (ambulanceTypes.js gives
 * mixed_stretchers and mixed_ambulatory for each vehicle: the planning load per run).
 */
export const GROUND_LOADING_MIX = {
  litter: 0.7,
  ambulatory: 0.3,
  status: CAPACITY_STATUS.PLACEHOLDER,
  basis: 'Existing rule of evacuationCapacityCalculator.calculateGroundCapacity (70% litter, 30% ambulatory); Dr. Tatar: patients per run are in the vehicle constants.'
};

/** Evacuation leg (Tab 3 section B.3 route key) served by each facility type's own ambulances. */
export const FACILITY_LEG = {
  ROLE1: 'r1_to_r2b',
  ROLE2_BASIC: 'r2b_to_r2e',
  ROLE2_ENHANCED: 'r2e_to_r3',
  ROLE3: 'r3_to_r4',
  status: CAPACITY_STATUS.PLACEHOLDER,
  basis: 'Each facility moves its patients on the next leg of the chain; to be confirmed.'
};

/** Vehicle category (ambulanceTypes spec.category, or Tab 3 asset class) to vehicle class. */
export const VEHICLE_CLASS_BY_CATEGORY = {
  WHEELED: 'LIGHT', LIGHT: 'LIGHT', PROTECTED: 'LIGHT', BUS: 'LIGHT',
  ARMORED: 'HEAVY', TRACKED: 'HEAVY', HEAVY: 'HEAVY', HEAVY_ARMORED: 'HEAVY', MRAP: 'HEAVY'
};

/**
 * Litter and sitting places per vehicle when a fleet is given only as counts (legacy evacuation_assets). Same values as
 * the ForceBuilder defaults in evacuationAssetAllocation.js (wheeled 2 + 4, armored 4 + 4).
 */
export const LEGACY_VEHICLE_PLACES = {
  LIGHT: { litter: 2, ambulatory: 4 },
  HEAVY: { litter: 4, ambulatory: 4 },
  status: CAPACITY_STATUS.PLACEHOLDER,
  basis: 'ForceBuilder default ambulance places (evacuationAssetAllocation.js ROLE1_ORGANIC_ASSETS).'
};

/**
 * The tactical evacuation analysis (Tab 3 B.0) counts ambulances by class (wheeled_light, wheeled_armored, tracked), not
 * by vehicle. The capacity of one vehicle of each class is taken from a reference vehicle in ambulanceTypes.js: the
 * Romanian fleet that is the app's default. PLACEHOLDER mapping, to be confirmed (other nations use other vehicles).
 */
export const TACTICAL_CLASS_REFERENCE_VEHICLE = {
  wheeled_light: { code: 'TABC79', vehicleClass: 'LIGHT' },
  wheeled_armored: { code: 'PIRANHA_V_MEDEVAC', vehicleClass: 'HEAVY' },
  tracked: { code: 'M113', vehicleClass: 'HEAVY' },
  status: CAPACITY_STATUS.PLACEHOLDER,
  basis: 'Reference vehicle per tactical ambulance class (ambulanceTypes.js); replaces the local table of the tactical module.'
};

/**
 * Refuelling time at turnaround for helicopter types whose spec has no `fuel.refuel_time_minutes`. Only the IAR-330 and
 * the UH-60 carry it (10 min each); the other ten types lack the data. PLACEHOLDER until the specs are completed.
 */
export const HELICOPTER_TURNAROUND = {
  defaultRefuelMin: 10,
  status: CAPACITY_STATUS.PLACEHOLDER,
  basis: 'IAR330 and UH60 specs: 10 min; ten other helicopter types have no refuel time in helicopterSpecs.js.'
};

export const listCapacityPlaceholders = () => [
  { id: 'ground_speed_gravel', basis: GROUND_SPEED_KPH.basis },
  { id: 'ground_daily_schedule', basis: GROUND_DAILY_SCHEDULE.basis },
  { id: 'ground_loading_mix', basis: GROUND_LOADING_MIX.basis },
  { id: 'facility_leg', basis: FACILITY_LEG.basis },
  { id: 'tactical_class_reference_vehicle', basis: TACTICAL_CLASS_REFERENCE_VEHICLE.basis },
  { id: 'helicopter_turnaround_refuel', basis: HELICOPTER_TURNAROUND.basis }
];
